import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

const timelineData = [
  { time: '7:02 AM', text: 'Shower idea about the pitch — captured.' },
  { time: '9:30 AM', text: 'Team sync notes sorted automatically.' },
  { time: '1:15 PM', text: 'Reminded: client contract due by EOD.' },
  { time: '6:40 PM', text: 'Noticed a good food stall — logged.' },
  { time: '11:00 PM', text: '51 thoughts processed. 14 tasks. 0 lost.', highlight: true },
];

const TimelineItem = ({ item, scrollYProgress, threshold }) => {
  const itemOpacity = useTransform(
    scrollYProgress,
    [threshold - 0.05, threshold],
    [0, 1]
  );
  const itemX = useTransform(
    scrollYProgress,
    [threshold - 0.05, threshold],
    [-20, 0]
  );
  const dotScale = useTransform(
    scrollYProgress,
    [threshold - 0.02, threshold + 0.02],
    [0, 1]
  );

  return (
    <div className="relative w-full flex items-center md:items-start md:flex-row flex-col">
      {/* Responsive dot rendering */}
      <motion.div
        className="absolute left-[-26px] md:left-[-1px] top-1/2 -translate-y-1/2 md:top-2 w-3 h-3 rounded-full bg-[#111] border-[2px] border-[#8B5CF6] shadow-[0_0_10px_rgba(139,92,246,0.5)] z-10"
        style={{ scale: dotScale }}
      />
      {/* Timestamp (Monochrome, subtle) */}
      <motion.div
        className="text-[#555] font-mono text-xs md:text-sm tracking-widest pl-2 md:pl-0 md:pr-12 md:w-[100px] md:text-right mt-1"
        style={{ opacity: itemOpacity, x: itemX }}
      >
        {item.time}
      </motion.div>
      {/* Content */}
      <motion.div
        className={`text-left flex-1 pl-2 md:pl-8 text-base md:text-lg tracking-tight ${item.highlight ? 'text-[#EDEDED] font-medium' : 'text-[#888] font-light'}`}
        style={{ opacity: itemOpacity, x: itemX }}
      >
        {item.highlight ? (
          <span className="flex items-center gap-2">
            {item.text}
          </span>
        ) : item.text}
      </motion.div>
    </div>
  );
};

/**
 * FutureVision — "A Real Day With Neuron"
 * 
 * Uses a sticky container to create a scroll-driven timeline.
 * The section itself is tall (e.g. 300vh) allowing scroll progress to
 * draw the timeline down.
 */
const FutureVision = () => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end end'],
  });

  // Calculate the progress of the drawing line
  // We want the line to draw from 0% to 100% between scroll 10% and 80%
  const lineHeightProgress = useTransform(scrollYProgress, [0.1, 0.8], ['0%', '100%']);

  // Calculate visibility for each item based on scroll thresholds
  // We map the 5 items smoothly across the 0.1 -> 0.8 scroll domain
  const itemThresholds = [0.15, 0.3, 0.45, 0.6, 0.75];

  return (
    <section
      ref={sectionRef}
      style={{
        height: '250vh', // Tall enough to feel substantial, but not exhausting
        position: 'relative',
        backgroundColor: 'transparent',
      }}
    >
      {/* ── STICKY CONTAINER (Always 100vh tall, stays in viewport) ── */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Ambient glow */}
        <div style={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 700, height: 500, pointerEvents: 'none',
          background: 'radial-gradient(ellipse, rgba(139,92,246,0.07) 0%, transparent 70%)',
          filter: 'blur(60px)',
          zIndex: 0
        }} />

        {/* ── Main Content Area ── */}
        <div className="flex flex-col flex-1 items-center justify-center px-6 md:px-12 relative z-10 w-full pt-20">
          <div className="w-full max-w-3xl mx-auto flex flex-col h-[70vh]">

            {/* Header */}
            <div className="text-center mb-16 flex-shrink-0">
              <span className="text-xs font-mono uppercase tracking-[0.3em] text-[#444] block mb-6">
                — A Real Day With Neuron —
              </span>
              <h2
                className="text-4xl md:text-5xl lg:text-6xl font-medium tracking-tighter text-[#EDEDED] mb-4"
                style={{ lineHeight: 1.05 }}
              >
                Built to think.{' '}
                <motion.span
                  style={{ color: '#8B5CF6' }}
                  animate={{ textShadow: ['0 0 20px rgba(139,92,246,0.3)', '0 0 60px rgba(139,92,246,0.6)', '0 0 20px rgba(139,92,246,0.3)'] }}
                  transition={{ duration: 3, repeat: Infinity }}
                >
                  Not to remember.
                </motion.span>
              </h2>
            </div>

            {/* Timeline Container */}
            <div className="relative flex-1 w-full max-w-lg mx-auto pl-[40px] md:pl-0 pt-4">

              {/* The Vertical Track (Ghost Line) */}
              <div
                className="absolute top-0 bottom-0 left-[20px] md:left-[100px] w-px bg-[#1A1A1A]"
              />

              {/* The Vertical Track (Glowing progress line that draws down) */}
              <motion.div
                className="absolute top-0 left-[20px] md:left-[100px] w-[2px] -ml-[0.5px] bg-[#8B5CF6]"
                style={{
                  height: lineHeightProgress,
                  boxShadow: '0 0 10px rgba(139,92,246,0.5)'
                }}
              />

              {/* Timeline Items */}
              <div className="flex flex-col justify-between h-full pb-8">
                {timelineData.map((item, index) => (
                  <TimelineItem
                    key={index}
                    item={item}
                    scrollYProgress={scrollYProgress}
                    threshold={itemThresholds[index]}
                  />
                ))}
              </div>

            </div>
          </div>
        </div>


      </div>
    </section>
  );
};

export default FutureVision;
